// Models
import {
  Filter,
  FilterOption,
  IsNotOneOfOperatorAndValue,
  IsOneOfOperatorAndValue,
} from 'app/modules/filters/models';
import { QueueType } from 'app/modules/queues/models';
import {
  InvestigationSource,
  InvestigationStatus,
} from 'app/modules/investigations/models';

// Components
import { DispositionFilterDisplay } from 'app/modules/filters/components/DispositionFilterDisplay';
import { SubdispositionFilterDisplay } from 'app/modules/filters/components/SubdispositionFilterDisplay';
import {
  U21DispositionSelect,
  U21SubdispositionAndOptionsSelect,
} from 'app/shared/u21-ui/components/dashboard';

// Constants
import {
  TAG_FILTER_OPTION,
  getAgentFilterOption,
  getEntityFilterOption,
  getInstrumentFilterOption,
  getQueueFilterOption,
} from 'app/modules/filters/filters';
import { FILTER_OPERATOR, FILTER_TYPE } from 'app/modules/filters/constants';

export const CASE_FILTER_KEYS = {
  ASSIGNEE: 'assignee',
  AMOUNT: 'amount',
  CREATED_AT: 'created_at',
  DISPOSITION: 'disposition',
  DISPOSITIONED_AT: 'dispositioned_at',
  ENTITY: 'entity',
  ID_TITLE: 'phrase',
  INSTRUMENT: 'instrument',
  QUEUE: 'queue',
  SOURCE: 'source',
  STATUS: 'status',
  SUBDISPOSITION: 'subdisposition',
  TAG: 'tag',
  WATCHERS: 'watchers',
} as const;

export const NATIVE_CASE_FILTER_KEYS_SET = new Set<string>(
  Object.values(CASE_FILTER_KEYS),
);

export const ALL_CASE_FILTER_OPTIONS: FilterOption[] = [
  // SC-118537: Amount filter temporarily hidden — case `amount_volume` no longer
  // populated after umbridge async Kafka path was removed. Restore once the
  // backing data is being written again.
  {
    key: CASE_FILTER_KEYS.ASSIGNEE,
    ...getAgentFilterOption('Assignee', true),
  },
  {
    key: CASE_FILTER_KEYS.CREATED_AT,
    label: 'Created at',
    type: FILTER_TYPE.DATE_RANGE,
  },
  {
    key: CASE_FILTER_KEYS.DISPOSITION,
    label: 'Disposition',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        Display: ({
          value,
        }: Omit<IsOneOfOperatorAndValue<string>, 'onChange'>) => {
          return <DispositionFilterDisplay value={value} />;
        },
        Input: ({ onChange, value }: IsOneOfOperatorAndValue<string>) => {
          return (
            <U21DispositionSelect
              autoFocus
              backdrop
              clearable={false}
              onChange={onChange}
              // case filters searches disposition via text but this is no longer
              // necessary since we have formalized dispositions. Apply a hack
              // so that users can select dispositions but internally it's just
              // searching dispositions via text
              useTitle
              multi
              value={value}
            />
          );
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: CASE_FILTER_KEYS.DISPOSITIONED_AT,
    label: 'Dispositioned at',
    type: FILTER_TYPE.DATE_RANGE,
  },
  {
    key: CASE_FILTER_KEYS.ENTITY,
    ...getEntityFilterOption(),
  },
  {
    key: CASE_FILTER_KEYS.ID_TITLE,
    label: 'ID / Title',
    type: FILTER_TYPE.TEXT,
  },
  {
    key: CASE_FILTER_KEYS.INSTRUMENT,
    ...getInstrumentFilterOption(),
  },
  {
    key: CASE_FILTER_KEYS.QUEUE,
    ...getQueueFilterOption(QueueType.CASE_QUEUE),
  },
  {
    key: CASE_FILTER_KEYS.SOURCE,
    label: 'Source',
    type: FILTER_TYPE.SELECT,
    customize: {
      [FILTER_OPERATOR.IS]: {
        inputProps: {
          options: [
            { text: 'Internal', value: InvestigationSource.INTERNAL },
            { text: 'External', value: InvestigationSource.EXTERNAL },
          ],
        },
      },
    },
  },
  {
    key: CASE_FILTER_KEYS.STATUS,
    label: 'Status',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        inputProps: {
          options: [
            { text: 'Open', value: InvestigationStatus.OPEN },
            { text: 'Closed', value: InvestigationStatus.CLOSED },
            { text: 'In Review', value: InvestigationStatus.IN_REVIEW },
          ],
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: { disabled: true },
    },
  },
  {
    key: CASE_FILTER_KEYS.SUBDISPOSITION,
    label: 'Subdisposition',
    type: FILTER_TYPE.MULTI_SELECT,
    customize: {
      [FILTER_OPERATOR.IS_ONE_OF]: {
        Display: ({
          operator,
          value,
        }: Omit<IsOneOfOperatorAndValue<number>, 'onChange'>) => {
          return (
            <SubdispositionFilterDisplay ids={value} operator={operator} />
          );
        },
        Input: ({ onChange, value }: IsOneOfOperatorAndValue<number>) => {
          return (
            <U21SubdispositionAndOptionsSelect
              autoFocus
              backdrop
              clearable={false}
              multi
              onChange={onChange}
              value={value}
            />
          );
        },
      },
      [FILTER_OPERATOR.IS_NOT_ONE_OF]: {
        Display: ({
          operator,
          value,
        }: Omit<IsNotOneOfOperatorAndValue<number>, 'onChange'>) => {
          return (
            <SubdispositionFilterDisplay ids={value} operator={operator} />
          );
        },
        Input: ({ onChange, value }: IsNotOneOfOperatorAndValue<number>) => {
          return (
            <U21SubdispositionAndOptionsSelect
              autoFocus
              backdrop
              clearable={false}
              multi
              onChange={onChange}
              value={value}
            />
          );
        },
      },
    },
  },
  {
    key: CASE_FILTER_KEYS.TAG,
    ...TAG_FILTER_OPTION,
  },
  {
    key: CASE_FILTER_KEYS.WATCHERS,
    ...getAgentFilterOption('Watchers', true),
  },
];

export const MY_CASE_FILTER_OPTIONS = ALL_CASE_FILTER_OPTIONS.filter(
  (i) => i.key !== CASE_FILTER_KEYS.ASSIGNEE,
);

export const CASE_QUEUE_CASE_FILTER_OPTIONS = ALL_CASE_FILTER_OPTIONS.filter(
  (i) => i.key !== CASE_FILTER_KEYS.QUEUE,
);

export const MY_CASE_DEFAULT_FILTERS: Filter[] = [
  {
    key: CASE_FILTER_KEYS.STATUS,
    operator: FILTER_OPERATOR.IS_ONE_OF,
    value: [InvestigationStatus.OPEN],
  },
];
